/**
 * EventTogglesSlot — Per-performance event assignment toggles, rendered inside
 * the wizard's Edit Performance modal (PerformanceConfigStep).
 *
 * Replaces the previous "events scoped to this perf" list (with inline
 * add/edit/delete) with a multi-select toggle list of ALL events defined on
 * the rodeo. Membership in `EventDraft.performance_assignments` for the
 * target perf drives each toggle's checked state, and flipping a toggle
 * patches the event via the rodeo store's `updateEvent` action.
 *
 * Master toggle semantics
 *   - Tri-state: all on, all off, or mixed.
 *   - Click flips to "all on" when any event is off; otherwise "all off".
 *     (Mixed → all on by design: easier to opt out of a few than to
 *     re-enable many.)
 *
 * Events are CREATED in Step 2 (Event Config). This slot is purely for
 * assigning existing events to the perf — no inline add button.
 */
import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useRodeoStore } from '@/state_management_and_stores/rodeo.store';

export interface EventTogglesSlotProps {
  /** The wizard-side `_key` of the performance being edited. */
  performanceKey: string;
}

export function EventTogglesSlot({ performanceKey }: EventTogglesSlotProps) {
  const events = useRodeoStore((s) => s.events);
  const updateEvent = useRodeoStore((s) => s.updateEvent);

  // Pending-delete events shouldn't appear in the toggle list — they're
  // about to be removed entirely on Save Draft.
  const visible = useMemo(
    () => events.filter((ec) => !ec._pendingDelete),
    [events],
  );

  const items = useMemo(
    () =>
      visible.map((ec) => ({
        key: ec._key,
        label: ec.event_type || 'Unnamed event',
        assigned: ec.performance_assignments.some(
          (a) => a.performance_key === performanceKey,
        ),
      })),
    [visible, performanceKey],
  );

  const allOn = items.length > 0 && items.every((i) => i.assigned);
  const allOff = items.every((i) => !i.assigned);
  const mixed = !allOn && !allOff;

  const setAssignment = (eventKey: string, assigned: boolean) => {
    const ec = events.find((e) => e._key === eventKey);
    if (!ec) return;
    const already = ec.performance_assignments.some(
      (a) => a.performance_key === performanceKey,
    );
    if (already === assigned) return;
    const nextAssignments = assigned
      ? [
          ...ec.performance_assignments,
          { performance_key: performanceKey, max_contestants: null },
        ]
      : ec.performance_assignments.filter(
          (a) => a.performance_key !== performanceKey,
        );
    updateEvent(eventKey, { performance_assignments: nextAssignments });
  };

  const handleToggleAll = () => {
    // Flip-to-all-on unless every event is already on. Mixed flips to all-on.
    const target = !allOn;
    for (const item of items) {
      if (item.assigned !== target) {
        setAssignment(item.key, target);
      }
    }
  };

  if (visible.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Events in this Performance</h3>
        <p className="text-sm text-muted-foreground italic">
          No events defined yet. Add events in the Events step first.
        </p>
      </div>
    );
  }

  const masterLabel = allOn
    ? 'All events enabled'
    : allOff
      ? 'No events enabled'
      : 'Some events enabled';

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Events in this Performance</h3>
        <p className="text-xs text-muted-foreground">
          Toggle which events run in this performance.
        </p>
      </div>

      {/* Master toggle */}
      <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-muted/30">
        <Label
          htmlFor="event-toggles-master"
          className="font-medium text-sm cursor-pointer"
        >
          Toggle all
          <span className="block text-xs font-normal text-muted-foreground">
            {masterLabel}
          </span>
        </Label>
        <Switch
          id="event-toggles-master"
          checked={allOn}
          onCheckedChange={handleToggleAll}
          aria-label="Toggle all events"
          // The Switch component is binary; mixed state is communicated via
          // the label copy above. Add a subtle ring when mixed so the
          // control still hints at the indeterminate state visually.
          className={mixed ? 'ring-2 ring-amber-400/60' : ''}
        />
      </div>

      {/* Per-event toggles */}
      <div className="space-y-1.5">
        {items.map((item) => {
          const id = `event-toggle-${item.key}`;
          return (
            <div
              key={item.key}
              className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 hover:bg-muted/40 transition-colors"
            >
              <Label
                htmlFor={id}
                className="flex-1 text-sm font-medium cursor-pointer truncate"
              >
                {item.label}
              </Label>
              <Switch
                id={id}
                checked={item.assigned}
                onCheckedChange={(checked) => setAssignment(item.key, checked)}
                aria-label={`Toggle ${item.label} for this performance`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EventTogglesSlot;
